/**
 * @file LegalProfileEditDialog.tsx
 * @description Редактор реквизитов продавца (phase09, F14).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  createLegalProfile,
  updateLegalProfile,
  type LegalProfileDto,
} from "../../lib/api-crm";

interface Props {
  mode: "create" | "edit";
  profile?: LegalProfileDto;
  onClose: () => void;
  onSaved: (p: LegalProfileDto) => void;
}

export function LegalProfileEditDialog({ mode, profile, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [displayName, setDisplayName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [inn, setInn] = useState("");
  const [kpp, setKpp] = useState("");
  const [ogrn, setOgrn] = useState("");
  const [legalAddress, setLegalAddress] = useState("");
  // phase19 — контактный телефон (fallback для блока самовывоза checkout).
  const [phone, setPhone] = useState("");
  const [vatPayer, setVatPayer] = useState(false);
  const [defaultVatRate, setDefaultVatRate] = useState<string>("");
  const [defaultSignerName, setDefaultSignerName] = useState("");
  const [defaultSignerPosition, setDefaultSignerPosition] = useState("");
  const [defaultSignerBasis, setDefaultSignerBasis] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (mode === "edit" && profile) {
      setDisplayName(profile.displayName);
      setLegalName(profile.legalName);
      setInn(profile.inn);
      setKpp(profile.kpp ?? "");
      setOgrn(profile.ogrn ?? "");
      setLegalAddress(profile.legalAddress);
      setPhone(profile.phone ?? "");
      setVatPayer(profile.vatPayer);
      setDefaultVatRate(profile.defaultVatRate ? String(profile.defaultVatRate) : "");
      setDefaultSignerName(profile.defaultSignerName);
      setDefaultSignerPosition(profile.defaultSignerPosition);
      setDefaultSignerBasis(profile.defaultSignerBasis);
      setIsDefault(profile.isDefault);
      setIsActive(profile.isActive);
    } else {
      setDisplayName("");
      setLegalName("");
      setInn("");
      setKpp("");
      setOgrn("");
      setLegalAddress("");
      setPhone("");
      setVatPayer(false);
      setDefaultVatRate("");
      setDefaultSignerName("");
      setDefaultSignerPosition("");
      setDefaultSignerBasis("");
      setIsDefault(false);
      setIsActive(true);
    }
  }, [mode, profile]);

  async function onSubmit() {
    setError(null);
    if (!displayName.trim() || !legalName.trim() || !inn.trim() || !legalAddress.trim()) {
      setError("Заполните: название, юр.имя, ИНН, юр.адрес.");
      return;
    }
    if (!/^(\d{10}|\d{12})$/.test(inn.trim())) {
      setError("ИНН — 10 цифр (юр.лица) или 12 цифр (ИП).");
      return;
    }
    if (kpp.trim() && !/^\d{9}$/.test(kpp.trim())) {
      setError("КПП — 9 цифр.");
      return;
    }
    if (ogrn.trim() && !/^(\d{13}|\d{15})$/.test(ogrn.trim())) {
      setError("ОГРН — 13 цифр (юр.лица) или 15 (ИП).");
      return;
    }
    if (!defaultSignerName.trim() || !defaultSignerPosition.trim() || !defaultSignerBasis.trim()) {
      setError("Подписант: имя, должность и основание обязательны (для УПД).");
      return;
    }
    const vatNum = defaultVatRate.trim() ? parseInt(defaultVatRate, 10) : undefined;
    if (vatNum !== undefined && (isNaN(vatNum) || vatNum < 0 || vatNum > 20)) {
      setError("Ставка НДС — 0/10/20.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        displayName: displayName.trim(),
        legalName: legalName.trim(),
        inn: inn.trim(),
        kpp: kpp.trim() || undefined,
        ogrn: ogrn.trim() || undefined,
        legalAddress: legalAddress.trim(),
        phone: phone.trim() || undefined,
        vatPayer,
        defaultVatRate: vatNum,
        defaultSignerName: defaultSignerName.trim(),
        defaultSignerPosition: defaultSignerPosition.trim(),
        defaultSignerBasis: defaultSignerBasis.trim(),
        isDefault,
      };
      const saved =
        mode === "create"
          ? await createLegalProfile(payload)
          : await updateLegalProfile(profile!.id, { ...payload, isActive });
      notify.success(mode === "create" ? "Профиль создан" : "Профиль обновлён");
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Новый профиль реквизитов" : "Редактирование профиля"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <fieldset className="flex flex-col gap-3">
            <legend className="label-editorial mb-2">Юр.данные</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-displayname">Короткое название</Label>
                <Input
                  id="lp-displayname"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Основной профиль"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-legalname">Юр.имя</Label>
                <Input
                  id="lp-legalname"
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder="ИП Иванов Иван Иванович"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-inn">ИНН</Label>
                <Input
                  id="lp-inn"
                  value={inn}
                  onChange={(e) => setInn(e.target.value)}
                  placeholder="123456789012"
                  inputMode="numeric"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-kpp">КПП (юр.лица)</Label>
                <Input
                  id="lp-kpp"
                  value={kpp}
                  onChange={(e) => setKpp(e.target.value)}
                  placeholder="123456789"
                  inputMode="numeric"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-ogrn">ОГРН / ОГРНИП</Label>
                <Input
                  id="lp-ogrn"
                  value={ogrn}
                  onChange={(e) => setOgrn(e.target.value)}
                  placeholder="1234567890123"
                  inputMode="numeric"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Label htmlFor="lp-addr">Юр.адрес</Label>
                <Input
                  id="lp-addr"
                  value={legalAddress}
                  onChange={(e) => setLegalAddress(e.target.value)}
                  placeholder="г. Челябинск, ул. Ленина, д. 1"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Label htmlFor="lp-phone">Контактный телефон (опц.)</Label>
                <Input
                  id="lp-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+7 (351) 000-00-00 — сайт показывает его в блоке самовывоза, если у склада нет своего"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={vatPayer}
                onChange={(e) => setVatPayer(e.target.checked)}
                className="accent-primary"
              />
              Плательщик НДС (УПД статус 1)
            </label>
            {vatPayer ? (
              <div className="flex flex-col gap-1.5 max-w-40">
                <Label htmlFor="lp-vat">Ставка НДС, %</Label>
                <Input
                  id="lp-vat"
                  type="number"
                  value={defaultVatRate}
                  onChange={(e) => setDefaultVatRate(e.target.value)}
                  placeholder="20"
                />
              </div>
            ) : null}
          </fieldset>

          <fieldset className="flex flex-col gap-3">
            <legend className="label-editorial mb-2">Подписант</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-signer-name">ФИО</Label>
                <Input
                  id="lp-signer-name"
                  value={defaultSignerName}
                  onChange={(e) => setDefaultSignerName(e.target.value)}
                  placeholder="Иванов И.И."
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="lp-signer-pos">Должность</Label>
                <Input
                  id="lp-signer-pos"
                  value={defaultSignerPosition}
                  onChange={(e) => setDefaultSignerPosition(e.target.value)}
                  placeholder="ИП / Директор"
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <Label htmlFor="lp-signer-basis">Основание</Label>
                <Input
                  id="lp-signer-basis"
                  value={defaultSignerBasis}
                  onChange={(e) => setDefaultSignerBasis(e.target.value)}
                  placeholder="Свидетельство ОГРНИП / Приказ № N от ДД.ММ.ГГГГ"
                />
              </div>
            </div>
          </fieldset>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="accent-primary"
            />
            По умолчанию для новых документов
          </label>

          {mode === "edit" ? (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="accent-primary"
              />
              Активен
            </label>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
